/**
 * BrioColorMode -- light / dark / system color-mode switcher (extra).
 *
 * Renders a three-state toggle. Persists choice to localStorage.
 * "system" defers to prefers-color-scheme by removing both theme
 * classes from <html>, letting brio's media-query tokens decide.
 * "light" and "dark" force their respective theme class override.
 *
 * Usage (auto-rendered chrome):
 *   <brio-color-mode></brio-color-mode>
 *
 * Shape modes (via class, per P7):
 *   default                  three-button group; cycle button not rendered
 *   class="is-cycle"         single cycle button at all sizes
 *   class="is-cycle-below-md" group above 48rem, cycle button below
 *
 * Button class override (auto-rendered chrome only):
 *   <brio-color-mode button-class="button is-primary is-sm is-icon"></brio-color-mode>
 *   The button-class attribute replaces the default class string on
 *   the generated buttons, so the switcher can render in any button
 *   recipe you want without hand-authoring the markup. The component's
 *   structural CSS keys off [data-theme-value] and the .button-group
 *   wrapper, not the presentation classes, so the override leaves the
 *   cycle-icon swap and responsive swap intact.
 *
 * Inner buttons render as .button.is-outlined.is-sm.is-icon -- the
 * same chrome as <docs-themepicker> -- so sizing and hover line up
 * across the header. The pill set is wrapped in a
 * <span class="button-group"> sibling of the cycle button; the wrapper
 * scopes button-group's segmented recipe to the three pills without
 * picking up the cycle button (which would otherwise be :last-child
 * and take the right-end radii from the dark pill).
 *
 * Usage (authored buttons -- bring your own icons / labels):
 *   <brio-color-mode>
 *       <span class="button-group">
 *           <button class="button is-outlined is-sm is-icon" type="button" data-theme-value="light" aria-label="Light">
 *               <svg>...</svg>
 *           </button>
 *           <button class="button is-outlined is-sm is-icon" type="button" data-theme-value="system" aria-label="System">
 *               <svg>...</svg>
 *           </button>
 *           <button class="button is-outlined is-sm is-icon" type="button" data-theme-value="dark" aria-label="Dark">
 *               <svg>...</svg>
 *           </button>
 *       </span>
 *       <button class="button is-outlined is-sm is-icon" type="button" data-theme-value="cycle" aria-label="Color mode">
 *           <span data-icon-for="light"><svg>...</svg></span>
 *           <span data-icon-for="system"><svg>...</svg></span>
 *           <span data-icon-for="dark"><svg>...</svg></span>
 *       </button>
 *   </brio-color-mode>
 *
 * Auto-rendered chrome is a deliberate exception to brio's "custom
 * elements orchestrate authored DOM, not create it" rule. This
 * component is primarily a drop-in affordance: requiring consumers
 * to hand-write three buttons with SVG icons in every header to
 * get a working switcher would be tedious. Authored DOM is still
 * supported for consumers who want to supply their own icons or
 * labels -- the element only generates default chrome when no
 * children are present.
 *
 * Pair with the no-flash init snippet (see docs/templates/partials/
 * head.html for the reference implementation) so the correct
 * theme class is applied before first paint.
 *
 * Programmatic:
 *   import { applyTheme, loadTheme } from "./brio-color-mode.js";
 *   applyTheme("dark");
 */
import { BrioBase } from '../brio-base.js';

const STORAGE_KEY = 'brio-color-mode';

// Cycle advances through these in order; wraps at the end.
const CYCLE_ORDER = ['light', 'system', 'dark'];

// Default class string for auto-rendered buttons. Override it
// per-instance with the host's `button-class` attribute to render the
// switcher in a different button recipe (a filled variant, another
// size, a different shape); see #renderDefaultButtons. The component's
// structural CSS keys off [data-theme-value] and .button-group, not
// these presentation classes, so an override doesn't break the
// cycle-icon swap or the responsive group/cycle swap.
const DEFAULT_BUTTON_CLASS = 'button is-neutral is-outlined is-sm is-icon';

const ICONS = {
    light: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>',
    system: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    dark: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
};

function nextCycleMode(current) {
    const idx = CYCLE_ORDER.indexOf(current);
    return CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
}

function save(mode) {
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        /* localStorage unavailable -- silent fallback */
    }
}

export function loadTheme() {
    try {
        return localStorage.getItem(STORAGE_KEY) || 'system';
    } catch {
        return 'system';
    }
}

export function applyTheme(mode) {
    const root = document.documentElement;
    root.classList.remove('light-theme', 'dark-theme');
    if (mode === 'light') {
        root.classList.add('light-theme');
    } else if (mode === 'dark') {
        root.classList.add('dark-theme');
    }
    // "system" -- leave both classes off so @media (prefers-color-scheme)
    // drives the decision via brio's token layer.

    document.querySelectorAll('brio-color-mode').forEach((el) => {
        el.setAttribute('data-current-mode', mode);
        el.querySelectorAll('button[data-theme-value]').forEach((btn) => {
            const value = btn.dataset.themeValue;
            // Cycle button isn't a toggle -- no aria-pressed. Explicit
            // pill buttons get aria-pressed reflecting their own mode.
            if (value === 'cycle') return;
            btn.setAttribute('aria-pressed', String(value === mode));
        });
    });
}

class BrioColorMode extends BrioBase {
    static tag = 'brio-color-mode';
    static {
        customElements.define(BrioColorMode.tag, BrioColorMode);
    }

    #onClick;

    connectedCallback() {
        if (!this.children.length) {
            this.#renderDefaultButtons();
        }

        this.#onClick = (e) => {
            const btn = e.target.closest('button[data-theme-value]');
            if (!btn) return;
            const value = btn.dataset.themeValue;
            const mode = value === 'cycle' ? nextCycleMode(loadTheme()) : value;
            applyTheme(mode);
            save(mode);
        };
        this.addEventListener('click', this.#onClick);

        applyTheme(loadTheme());
    }

    disconnectedCallback() {
        if (this.#onClick) this.removeEventListener('click', this.#onClick);
    }

    #renderDefaultButtons() {
        const title = 'Toggle color mode (light, dark, system)';
        const buttonClass = this.getAttribute('button-class') || DEFAULT_BUTTON_CLASS;
        const isCycleOnly = this.classList.contains('is-cycle');
        const isResponsive = this.classList.contains('is-cycle-below-md');

        // Pill group -- skipped in always-cycle mode. Wrapped in its own
        // .button-group so the segmented recipe (first/last radii, edge
        // overlap) applies only to the three pills; the cycle button
        // sits outside as a sibling.
        if (!isCycleOnly) {
            const group = document.createElement('span');
            group.className = 'button-group';
            CYCLE_ORDER.forEach((mode) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = buttonClass;
                btn.dataset.themeValue = mode;
                btn.setAttribute(
                    'aria-label',
                    `${mode.charAt(0).toUpperCase() + mode.slice(1)} mode`
                );
                btn.setAttribute('aria-pressed', 'false');
                btn.title = title;
                btn.innerHTML = ICONS[mode];
                group.appendChild(btn);
            });
            this.appendChild(group);
        }

        // Cycle button -- rendered for is-cycle (always shown) and
        // is-cycle-below-md (revealed below the breakpoint via CSS).
        // Default mode never needs it. Wraps all three icons; CSS
        // reveals the one matching the parent's data-current-mode.
        if (isCycleOnly || isResponsive) {
            const cycle = document.createElement('button');
            cycle.type = 'button';
            cycle.className = buttonClass;
            cycle.dataset.themeValue = 'cycle';
            cycle.setAttribute('aria-label', 'Color mode (click to cycle)');
            cycle.title = title;
            cycle.innerHTML = CYCLE_ORDER.map(
                (mode) => `<span data-icon-for="${mode}">${ICONS[mode]}</span>`
            ).join('');
            this.appendChild(cycle);
        }
    }
}
